import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getSsoConfig, startMicrosoftLogin, completeMicrosoftLoginIfReturning } from '../services/sso';
import Logo from '../components/Logo';

// Sans réseau, aucune connexion n'est possible. Le dire franchement évite
// d'envoyer l'utilisateur chercher un problème de son côté.
const MESSAGE_HORS_LIGNE = 'Pas de connexion réseau. La connexion sera possible une fois en ligne.';

export default function LoginPage() {
  const [error, setError] = useState('');
  const [ssoLoading, setSsoLoading] = useState(false);
  // Distinct de ssoLoading : traiter un éventuel retour d'Azure demande
  // d'initialiser MSAL, ce qui prenait quelques centaines de millisecondes
  // pendant lesquelles la page n'affichait qu'un « Chargement... ». Le bouton
  // apparaît maintenant dès que la configuration est connue, brièvement
  // désactivé plutôt qu'absent.
  const [retourEnCours, setRetourEnCours] = useState(true);
  // Trois états et non un booléen : tant que la configuration n'est pas
  // revenue, afficher « indisponible » ferait clignoter un message alarmant
  // sur une page qui va très bien.
  const [ssoEtat, setSsoEtat] = useState('chargement');
  const { loginSso } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    getSsoConfig()
      .then(c => setSsoEtat(c.enabled ? 'actif' : 'indisponible'))
      .catch(() => setSsoEtat('indisponible'));

    // On revient peut-être d'un redirect Azure (loginRedirect) : si oui, on
    // termine la connexion avant même que l'utilisateur ne voie la page.
    completeMicrosoftLoginIfReturning()
      .then(session => {
        if (session) { loginSso(session); navigate('/'); }
      })
      .catch(e => {
        if (!navigator.onLine) { setError(MESSAGE_HORS_LIGNE); return; }
        // Le message seul : l'objet d'erreur axios complet embarque la requête,
        // donc l'en-tête Authorization et l'id_token Microsoft.
        console.error('[SSO] Connexion Microsoft échouée :', e?.message || e);
        setError(e.response?.data?.message || e.errorMessage || e.message || 'Connexion Microsoft impossible');
      })
      .finally(() => setRetourEnCours(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMicrosoftLogin = async () => {
    setError('');
    setSsoLoading(true);
    try {
      await startMicrosoftLogin(); // navigue hors de la page : rien ensuite ne s'exécute normalement
    } catch (e) {
      if (!navigator.onLine) { setError(MESSAGE_HORS_LIGNE); }
      else {
        console.error('[SSO] Connexion Microsoft échouée :', e?.message || e);
        setError(e.response?.data?.message || e.errorMessage || e.message || 'Connexion Microsoft impossible');
      }
      setSsoLoading(false);
    }
  };

  return (
    <div style={styles.wrapper}>
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.logo}>
            <Logo size={96} />
          </div>
          <h1 style={styles.title}>Portail Entreprise</h1>
          <p style={styles.subtitle}>Connectez-vous avec votre compte Microsoft 365</p>
        </div>

        {error && <div style={styles.error}>{error}</div>}

        {ssoEtat === 'chargement' && (
          <div style={styles.attente}>Chargement...</div>
        )}

        {ssoEtat === 'actif' && (
          <button type="button" onClick={handleMicrosoftLogin} disabled={ssoLoading || retourEnCours} style={styles.msButton}>
            <MicrosoftLogo />
            {ssoLoading ? 'Connexion...' : 'Se connecter avec Microsoft'}
          </button>
        )}

        {ssoEtat === 'indisponible' && (
          <div style={styles.indisponible}>
            La connexion Microsoft est indisponible pour le moment.
            <br />
            Contactez le support informatique.
          </div>
        )}
      </div>
    </div>
  );
}

function MicrosoftLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

const styles = {
  wrapper: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #062259 0%, #0F56E3 60%, #019EF7 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
  },
  card: {
    background: '#ffffff',
    borderRadius: 16,
    padding: '48px 40px',
    width: '100%',
    maxWidth: 420,
    boxShadow: '0 20px 60px rgba(6,34,89,0.3)',
  },
  header: { textAlign: 'center', marginBottom: 32 },
  logo: { display: 'flex', justifyContent: 'center', margin: '0 auto 16px' },
  title: { fontSize: 24, fontWeight: 700, color: '#062259', marginBottom: 8 },
  subtitle: { fontSize: 14, color: '#5f6673' },
  error: {
    background: '#fff0ed',
    color: '#E46138',
    padding: '10px 14px',
    borderRadius: 8,
    fontSize: 14,
    border: '1px solid #E46138',
    textAlign: 'center',
    marginBottom: 16,
  },
  attente: { textAlign: 'center', fontSize: 14, color: '#5f6673', padding: '12px 0' },
  indisponible: {
    background: '#f4f6f9',
    color: '#5f6673',
    borderRadius: 8,
    padding: '14px 16px',
    fontSize: 13,
    lineHeight: 1.5,
    textAlign: 'center',
  },
  msButton: {
    width: '100%', padding: '14px',
    background: '#fff', color: '#1a1a2e',
    border: '1.5px solid #D9D9D9', borderRadius: 8,
    fontSize: 15, fontWeight: 600, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
  },
};
